<template>
  <div
    v-if="isRouteLoading"
    class="bg-bluedark-600 fixed top-0 left-0 z-50 h-1 transition-all duration-300 ease-out"
    :style="{ width: `${routeLoadingProgress}%` }"
  />

  <div class="hidden h-screen w-screen sm:block" :style="[brandingStyles]">
    <div class="flex h-full w-full">
      <!-- AI chat split-pane outlet: HeaderAI teleports the panel here. First
           in DOM so the Teleport target exists before any view mounts;
           order-2 keeps it visually on the right. Animating the width here
           squeezes the main pane smoothly on toggle. -->
      <div
        id="ai-chat-outlet"
        data-test="ai-chat-outlet"
        class="order-2 h-full shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out"
        :class="isAiPanelVisible ? 'w-[640px]' : 'w-0'"
      />

      <div class="order-1 h-full min-w-0 flex-1">
        <router-view v-slot="{ Component }">
      <transition
        appear
        mode="out-in"
        enter-active-class="transition ease-out duration-200"
        enter-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition ease-in duration-200"
        leave-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isLoading"
          class="flex h-full w-full flex-col items-center overflow-hidden"
          :class="isEmbedded ? 'justify-center' : 'justify-start gap-4'"
        >
          <!-- Embedded mode: simple centered spinner -->
          <div
            v-if="isEmbedded"
            class="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"
          ></div>
          <!-- Regular mode: animated loader with logout message -->
          <template v-else>
            <img src="@/assets/Loader.gif" class="mt-20 w-40" alt="Loading the app" />
            <section v-show="showLogoutMsg" class="flex flex-col items-center justify-start gap-4">
              <span class="text-base font-medium text-gray-500">
                Having trouble logging in? Log out and try logging in again. Apologies for the
                inconvenience.
              </span>
              <BaseButton color="blue-light" @click="logout">Log out</BaseButton>
            </section>
          </template>
        </div>
        <component
          :is="getLayoutComponent(Component)"
          v-else
          :key="route.path + JSON.stringify(route.params) + route.query.hub + route.query.list"
        >
          <component :is="Component" />
        </component>
      </transition>
        </router-view>
      </div>
    </div>

    <CalendlyModal />
  </div>

  <MobileLock class="sm:hidden" />

  <VueQueryDevtools button-position="bottom-right" />

  <HierarchyContext v-if="isAuthenticated && boardId" :board-id="boardId" />

  <ZoomWarning v-if="isAuthenticated" />

  <ToastDisplay />
</template>

<script setup>
import BaseButton from '@/components/BaseButton.vue'
import ToastDisplay from '@/components/ToastDisplay.vue'
import useBoard from '@/hooks/use-board.js'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useIdle } from '@vueuse/core'
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import HierarchyContext from '@/contexts/HierarchyContext.vue'
import useNavigation from '@/hooks/use-navigation.js'
import { useEmbedMode } from '@/hooks/use-embed-mode'
import { useConnectSocket } from '@/hooks/use-notifications'
import { useAiPanel } from '@/hooks/use-ai-panel'

const CalendlyModal = defineAsyncComponent(() => import('@/components/CalendlyModal.vue'))
const MobileLock = defineAsyncComponent(() => import('@/components/MobileLock.vue'))
const ZoomWarning = defineAsyncComponent(() => import('@/components/ZoomWarning.vue'))

// This has to be attached at the root so that the activity time is updated correctly
const { lastActive } = useIdle()
const { getters, dispatch } = useStore()
const { boardId } = useBoard()
const { isNavigating: isRouteLoading, routeLoadingProgress } = useNavigation()
const route = useRoute()
const { isEmbedded } = useEmbedMode()
const { visible: isAiPanelVisible } = useAiPanel()

useConnectSocket()

const isLoading = computed(() => getters.isAppLoading)
const isAuthenticated = computed(() => getters.isLoggedIn)

const brandingStyles = computed(() => {
  const branding = getters.getHubTheme
  return branding ? { '--color-agentnoon-primary-700': branding?.primary } : {}
})

const showLogoutMsg = ref(false)
/**
 * Get the appropriate layout component based on route metashowGlobalLandingBanner
 */
const getLayoutComponent = (Component) => {
  if (isEmbedded.value && route.meta?.embedLayout) {
    return route.meta.embedLayout
  }

  return route.meta?.layout || Component
}

// Watch for boardId changes to fetch board settings and feature access
watch(
  boardId,
  () => {
    if (!boardId.value) return

    dispatch('fetchSettings', { boardId: boardId.value })
    dispatch('branding/fetch', { boardId: boardId.value })
    dispatch('accessScopes/fetchFeatures', { boardId: boardId.value })
  },
  { immediate: true }
)

// Track user activity for session management
watch(
  () => lastActive.value,
  () => localStorage.setItem('lastActive', lastActive.value)
)

let timer = null

/**
 * Starts the loading process by changing the isAppLoading state to true
 * and setting a timer to show a logout message after 5 seconds.
 */
const startLoading = async () => {
  await dispatch('changeIsAppLoading', true)
  timer = setTimeout(() => {
    showLogoutMsg.value = true
  }, 5000)
}

/**
 * Stops the loading process by clearing the timer and changing the isAppLoading state to false
 */
const stopLoading = async () => {
  clearTimeout(timer)
  await dispatch('changeIsAppLoading', false)
  showLogoutMsg.value = false
}

/**
 * Watches the isLoading state and performs actions based on its value.
 */
watch(isLoading, (newValue) => {
  if (!newValue) {
    clearTimeout(timer)
    showLogoutMsg.value = false
  }
})

// Event listener for navigation elements to prefetch data on hover
onMounted(async () => {
  const { prefetchRouteData } = useNavigation()

  document.addEventListener('mouseover', (e) => {
    // Check if the hovered element is a link
    if (e.target.tagName === 'A' && e.target.href) {
      const path = e.target.getAttribute('href')
      if (path && path.startsWith('/')) {
        prefetchRouteData(path)
      }
    }
  })

  if (window.mixpanel) window.mixpanel.track('app_open')

  // Start with loading state
  await startLoading()

  // Wait for initialization and authentication to complete
  // The router navigation guards will handle redirects based on auth state
  setTimeout(() => {
    stopLoading()
  }, 1000)
})

/**
 * Logs out the user by calling the auth service logout
 * and redirecting to the home page.
 */
const logout = async () => {
  if (window.mixpanel) {
    window.mixpanel.track('app_logout_due_to_stuck_at_loading')
  }

  // Clear embed destination from sessionStorage
  sessionStorage.removeItem('embed-destination')

  await dispatch('logout')
  await stopLoading()

  location.href = '/'
}
</script>

<style>
body {
  /* without this line, the font resets to default */
  font-family: Inter, 'sans-serif';
}
</style>
